import { motion } from "framer-motion";
import { Building2, Crown } from "lucide-react";

const generations = [
  {
    era: "1880s",
    generation: "I",
    title: "Foundation",
    description: "The first Viršilas craftsmen establish their practice in Lithuania—stone by stone, a legacy begins.",
  },
  {
    era: "1910s",
    generation: "II",
    title: "Mastery",
    description: "Traditional building arts refined across decades. Regional recognition earned through unwavering dedication.",
  },
  {
    era: "1940s",
    generation: "III",
    title: "Perseverance",
    description: "Through upheaval and reconstruction, the family commitment endures. Communities rebuilt, bonds strengthened.",
  },
  {
    era: "1970s",
    generation: "IV",
    title: "Evolution",
    description: "Modern methodologies embraced without abandoning foundational principles. Craft meets innovation.",
  },
  {
    era: "1990s",
    generation: "V",
    title: "Expansion",
    description: "European markets entered with measured ambition. International developments signal a new chapter.",
  },
  {
    era: "2020s",
    generation: "VI",
    title: "Convergence",
    description: "Three generations operating in concert. 140 years of heritage applied to global ultra-luxury real estate.",
  },
];

export const HeritageSection = () => {
  return (
    <section id="heritage" className="py-24 lg:py-32 bg-[#1a1a1a] text-cream overflow-hidden relative">
      {/* Subtle texture overlay */}
      <div className="absolute inset-0 opacity-[0.02]">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, currentColor 0.5px, transparent 0)`,
          backgroundSize: '24px 24px'
        }} />
      </div>

      <div className="container mx-auto px-6 lg:px-12 relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className="w-12 h-px bg-gradient-to-r from-transparent to-gold-light/60" />
            <p className="font-body text-xs uppercase tracking-[0.4em] text-gold-light/80">
              Lineage
            </p>
            <div className="w-12 h-px bg-gradient-to-l from-transparent to-gold-light/60" />
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light mb-8 leading-tight tracking-tight">
            Six Generations of
            <span className="block italic font-normal text-gold-light mt-2">Quiet Excellence</span>
          </h2>
          <p className="font-body text-cream/50 max-w-2xl mx-auto leading-relaxed text-lg">
            Our understanding of generational investment is not theoretical—it is biographical. 
            The Viršilas legacy has been built across 140 years of continuous practice.
          </p>
        </motion.div>

        {/* Timeline */}
        <div className="relative max-w-4xl mx-auto">
          {/* Center Line */}
          <div className="absolute left-8 lg:left-1/2 transform lg:-translate-x-1/2 w-px h-full bg-gradient-to-b from-transparent via-gold-light/30 to-transparent" />
          
          <div className="space-y-6 lg:space-y-0">
            {generations.map((gen, index) => (
              <motion.div
                key={gen.generation}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, delay: index * 0.08 }}
                className={`relative lg:flex items-center py-6 lg:py-8 ${
                  index % 2 === 0 ? "lg:flex-row" : "lg:flex-row-reverse"
                }`}
              >
                {/* Content */}
                <div className={`lg:w-1/2 pl-20 lg:pl-0 ${index % 2 === 0 ? "lg:pr-20 lg:text-right" : "lg:pl-20"}`}>
                  <motion.div 
                    initial={{ opacity: 0, x: index % 2 === 0 ? 20 : -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.08 + 0.1 }}
                    className="group"
                  >
                    <p className="font-body text-[10px] uppercase tracking-[0.3em] text-gold-light/60 mb-2">
                      {gen.era}
                    </p>
                    <h3 className="font-display text-xl lg:text-2xl text-cream mb-2 tracking-wide">
                      <span className="text-gold-light font-light">{gen.generation}.</span>{" "}
                      <span className="font-normal">{gen.title}</span>
                    </h3>
                    <p className="font-body text-cream/40 text-sm leading-relaxed max-w-sm lg:max-w-none">
                      {gen.description}
                    </p>
                  </motion.div>
                </div>

                {/* Center Marker */}
                <div className="absolute left-8 lg:left-1/2 transform -translate-x-1/2 flex items-center justify-center">
                  <motion.div 
                    initial={{ scale: 0.8 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.08 }}
                    className="w-4 h-4 rounded-full bg-[#1a1a1a] border border-gold-light/40 flex items-center justify-center"
                  >
                    <div className="w-1.5 h-1.5 rounded-full bg-gold-light/60" />
                  </motion.div>
                </div>

                {/* Spacer for opposite side */}
                <div className="lg:w-1/2 hidden lg:block" />
              </motion.div>
            ))}
          </div>
        </div>

        {/* Bottom Badge */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="mt-24 text-center"
        >
          <div className="inline-flex items-center gap-5 border border-cream/10 px-10 py-5 bg-cream/[0.02]">
            <Building2 className="w-5 h-5 text-gold-light/70" />
            <div className="w-px h-8 bg-cream/10" />
            <div className="text-left">
              <p className="font-display text-base text-cream/90 tracking-wide">140 Years of Continuous Practice</p>
              <p className="font-body text-xs text-cream/40 tracking-wide mt-0.5">Three Generations in Active Partnership</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default HeritageSection;
